import type { EncodingFormat } from "../../types";
import { decodeBase58, encodeBase58 } from "./base58";
import { decodeBase64, encodeBase64 } from "./base64";
import { decodeHex, encodeHex } from "./hex";
import { decodeStrKey, encodeEd25519PublicKey } from "./strkey";

export * from "./base58";
export * from "./base64";
export * from "./hex";
export * from "./strkey";

/**
 * Environment-agnostic byte encoding function that works in both browser and Node.js.
 * Supports multiple encoding formats including base58, hex, and base64.
 *
 * @param bytes - The Uint8Array of bytes to encode
 * @param encoding - The encoding format to use (base58, hex, or base64)
 * @returns The encoded string representation
 * @throws {Error} If an unsupported encoding format is specified
 */
export function encodeBytes(
	bytes: Uint8Array,
	encoding: EncodingFormat,
): string {
	switch (encoding) {
		case "base58":
			return encodeBase58(bytes);
		case "hex":
			return encodeHex(bytes);
		case "base64":
			return encodeBase64(bytes);
		case "strkey":
			return encodeEd25519PublicKey(bytes);
		default:
			throw new Error(`Unsupported encoding: ${encoding}`);
	}
}

/**
 * Environment-agnostic byte decoding function that works in both browser and Node.js.
 * Supports multiple encoding formats including base58, hex, and base64.
 *
 * @param encoded - The encoded string to decode
 * @param encoding - The encoding format to use (base58, hex, or base64)
 * @returns The decoded bytes as a Uint8Array
 * @throws {Error} If an unsupported encoding format is specified
 */
export function decodeBytes(
	encoded: string,
	encoding: EncodingFormat,
): Uint8Array {
	switch (encoding) {
		case "base58":
			return decodeBase58(encoded);
		case "hex":
			return decodeHex(encoded);
		case "base64":
			return decodeBase64(encoded);
		case "strkey": {
			const result = decodeStrKey(encoded);
			return result.data;
		}
		default:
			throw new Error(`Unsupported encoding: ${encoding}`);
	}
}
